<template>
  <div class="operation-report-show-body-form-item flex flex-col">
    <div
      class="form-item-label aisea-text-16-700-dark-gray"
      :class="{
        required: required,
        'is-editing': isEditing,
        'has-errors': hasErrors,
      }"
    >
      {{ label }}
    </div>

    <div class="form-item-value-unit-edit flex aisea-text-16-400-dark-gray">
      <div class="form-item-value-edit w-full flex flex-col date-multi-fields">
        <div
          v-for="(row, index) in rows"
          :key="index"
          class="date-multi-row flex items-center"
        >
          <input
            type="date"
            v-model="row.d"
            :disabled="disabled"
            class="date-multi-input"
          />
          <button
            type="button"
            class="date-multi-btn"
            :disabled="disabled"
            @click="removeRow(index)"
          >
            削除
          </button>
        </div>
        <button
          type="button"
          class="date-multi-btn date-multi-add"
          :disabled="disabled"
          @click="addRow"
        >
          ＋ 日付を追加
        </button>
        <input
          class="validate-hidden-input"
          type="hidden"
          :name="name"
          v-model="value"
          v-validate="vValidate"
          :disabled="disabled"
        />
      </div>
    </div>
    <div
      class="nullable-label flex items-center aisea-text-16-400-dark-gray"
      v-if="hasNullableLabel"
    >
      <vs-checkbox
        v-model="nullableChecked"
        :color="checkBoxColor"
        :disabled="disabled"
        @change="onChange"
      >
        <span>{{ nullableLabel }}</span>
      </vs-checkbox>
    </div>
  </div>
</template>

<script>
import isEmpty from "lodash/isEmpty";
import isString from "lodash/isString";
import { ColorMixin } from "@/mixins";

const { AISEA_COLOR } = ColorMixin.data();

const EVENTS = { CHANGE: "change" };

// DATE_MULTI_SELECT_FROM_RANGE(15) の値: value_json ["YYYY-MM-DD", ...]
// API 側で参照 DATE_RANGE_PICKER の範囲制限方式が未確定のため、当面は自由複数日選択とする。
export default {
  name: "FormItemDateMultiSelect",
  inheritAttrs: false,
  props: {
    label: { type: String, default: "" },
    required: { type: Boolean, default: false },
    name: { type: String, required: true },
    initialValue: { type: [Array, String], required: false },
    unit: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    isEditing: { type: Boolean, default: false },
    nullableLabel: { type: String, required: false },
    isNullableChecked: { type: Boolean, default: false },
    hasReportFormVersionItemValue: { type: Boolean, default: false },
    defaultValue: { type: [Array, String], required: false },
  },
  data() {
    return {
      rows: [], // [{ d: "YYYY-MM-DD" }]
      value: "", // validation 用シリアライズ値
      nullableChecked: false,
    };
  },
  watch: {
    initialValue: {
      handler() {
        this.applyInitialValue();
      },
      immediate: true,
      deep: true,
    },
    isNullableChecked: {
      handler() {
        this.nullableChecked = this.isNullableChecked;
      },
      immediate: true,
    },
    rows: {
      handler() {
        this.recompute();
      },
      deep: true,
    },
    vValidate() {
      this.$nextTick(async () => {
        await this.$validator.validateAll();
      });
    },
  },
  computed: {
    selectedDates() {
      return this.rows.map(r => r.d).filter(d => d != null && d !== "");
    },
    emittedValue() {
      const ds = this.selectedDates;
      return ds.length > 0 ? ds : null;
    },
    hasUnit() {
      return isString(this.unit) && !isEmpty(this.unit);
    },
    isNullable() {
      if (!this.hasNullableLabel) return !this.required;
      return this.nullableChecked === true;
    },
    vValidate() {
      return this.required && !this.isNullable ? "required" : "";
    },
    hasErrors() {
      return this.errors.has(this.name);
    },
    hasNullableLabel() {
      return isString(this.nullableLabel) && !isEmpty(this.nullableLabel);
    },
    checkBoxColor() {
      return AISEA_COLOR.BLUE;
    },
  },
  methods: {
    parseValue(v) {
      if (Array.isArray(v)) return v;
      if (isString(v) && v !== "") {
        try {
          const parsed = JSON.parse(v);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      }
      return [];
    },
    applyInitialValue() {
      const arr = this.parseValue(this.initialValue);
      this.rows = arr.map(d => ({ d: d || "" }));
      this.value = this.selectedDates.join(",");
    },
    addRow() {
      if (this.disabled) return;
      this.rows.push({ d: "" });
    },
    removeRow(index) {
      if (this.disabled) return;
      this.rows.splice(index, 1);
    },
    recompute() {
      this.value = this.selectedDates.join(",");
      this.onChange();
    },
    onChange() {
      if (this.disabled) return;
      this.$emit(EVENTS.CHANGE, {
        value: this.emittedValue,
        nullableChecked: this.nullableChecked,
      });
    },
  },
};
</script>
<style lang="scss" scoped>
@import "@/assets/scss/aisea/_variables.scss";

.operation-report-show-body-form-item {
  min-height: 90px;
  padding: 18px 0;
  row-gap: 4px;

  .form-item-value-unit-edit {
    gap: 18px;
    padding-right: 18px;
  }

  .date-multi-fields {
    gap: 8px;
  }

  .date-multi-row {
    gap: 8px;
  }

  .date-multi-input {
    padding: 10px 12px;
    border: 1px solid $aisea-light-gray;
    border-radius: 6px;
    font-size: 16px;
  }

  .date-multi-btn {
    padding: 6px 12px;
    border: 1px solid $aisea-light-gray;
    border-radius: 6px;
    font-size: 14px;
    background: #fff;
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }

  .date-multi-add {
    align-self: flex-start;
  }

  .form-item-label.is-editing {
    &.has-errors {
      color: $aisea-red;
    }
    &.required:after {
      content: " *";
      font-size: 14px;
      color: $aisea-red;
      font-weight: 700;
      font-family: $aisea-font-family;
    }
  }
  .nullable-label {
    gap: 20px;
  }
}
</style>
